import { useEffect, useState } from 'react';

import type { PdfDocumentProxy } from './pdfjs';

export interface PdfDocumentState {
  document: PdfDocumentProxy | null;
  progress: number | null;
  loading: boolean;
  error: string | null;
}

export function usePdfDocument(url: string): PdfDocumentState {
  const [state, setState] = useState<PdfDocumentState>({ document: null, progress: null, loading: true, error: null });

  useEffect(() => {
    let active = true;
    let loadingTask: { destroy: () => void } | null = null;
    let loadedDocument: PdfDocumentProxy | null = null;
    setState({ document: null, progress: null, loading: true, error: null });

    void import('./pdfjs').then(({ pdfjs }) => {
      if (!active) return null;
      const task = pdfjs.getDocument({ url });
      loadingTask = task;
      task.onProgress = ({ loaded, total }) => {
        if (active) setState((current) => ({ ...current, progress: total > 0 ? Math.round((loaded / total) * 100) : null }));
      };
      return task.promise;
    }).then((document) => {
      if (!document) return;
      loadedDocument = document;
      if (active) setState({ document, progress: 100, loading: false, error: null });
      else void document.destroy();
    }).catch(() => {
      if (!active) return;
      setState({ document: null, progress: null, loading: false, error: 'Không thể mở tài liệu PDF.' });
    });

    return () => {
      active = false;
      if (loadingTask) loadingTask.destroy();
      if (loadedDocument) void loadedDocument.destroy();
    };
  }, [url]);

  return state;
}
